import React, { useEffect, useState } from 'react';

import { BrowserRouter as Router, Routes, Route, useNavigate, Outlet } from 'react-router-dom';
import Marketplace from './pages/Marketplace';
import AgentDashboard from './pages/AgentDashboard';
import OrderPage from './pages/OrderPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import JoinAgent from './pages/JoinAgent';
import DerivCallback from './pages/DerivCallback';
import EmailVerification from './pages/EmailVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Profile from './pages/Profile';
import Sessions from './pages/Sessions';
import Admin from './pages/Admin';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import History from './pages/History';
import RequestListPage from './pages/RequestListPage';
import TransactionDetailPage from './pages/TransactionDetailPage';
import AgentProfilePage from './pages/AgentProfilePage';
import AgentOrders from './pages/agent/AgentOrders';
import AgentClients from './pages/agent/AgentClients';
import AgentHistory from './pages/agent/AgentHistory';
import AgentCommissions from './pages/agent/AgentCommissions';
import ClientCommissions from './pages/client/ClientCommissions';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedLayout from './components/RoleBasedLayout';
import DerivLinking from './pages/DerivLinking';
import DerivGuard from './components/DerivGuard';
import AgentOnboardingFee from './pages/agent/AgentOnboardingFee';
import KYCVerification from './pages/KYCVerification';
import Support from './pages/Support';
import SupportCompose from './pages/SupportCompose';
import Onboarding from './pages/Onboarding';
import RoleLoader from './components/RoleLoader';
import bg1 from './stars-bg.jpg';
import bg2 from './stars-bg-2.jpg';
import bg3 from './stars-bg-3.jpg';

const backgrounds = [bg1, bg2, bg3];

const HomeRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const role = typeof user?.role === 'object' ? user.role?.name : user?.role;
    if (role === 'agent') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  return <Marketplace />;
};

function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const randomBg = backgrounds[Math.floor(Math.random() * backgrounds.length)];
    document.body.style.backgroundImage = `url(${randomBg})`;
    document.body.style.backgroundSize = '100% auto';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundPosition = 'top center';
    document.body.style.minHeight = '100vh';
    document.body.style.backgroundColor = '#121212';
  }, []);

  // Theme Effect
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <AuthProvider>
      <WebSocketProvider>
        <ToastProvider>
        <NotificationProvider>
          <Router>
            <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/join/:agentId" element={<JoinAgent />} />
          <Route path="/deriv/callback" element={<DerivCallback />} />
          <Route path="/deriv" element={<DerivCallback />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Admin Routes - Self-Managed Auth */}
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/orders/:orderId" element={<AdminOrderDetail />} />

          {/* Protected Route: Must be logged in */}
          <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
            
            {/* Syncing Gate */}
            <Route path="/auth-sync" element={<RoleLoader />} />

            {/* Deriv Linking Gate */}
            <Route path="/link-deriv" element={<DerivLinking />} />

            {/* Onboarding Gate (Protected but outside DerivGuard loop) */}
            <Route element={<RoleBasedLayout isDark={isDark} toggleTheme={toggleTheme} />}>
               <Route path="/onboarding" element={<Onboarding />} />
            </Route>

            {/* Main App Routes: Must be logged in AND have Deriv linked */}
            <Route element={<DerivGuard />}>
              <Route element={<RoleBasedLayout isDark={isDark} toggleTheme={toggleTheme} />}>
                <Route path="/" element={<HomeRedirect />} />
                
                {/* Common but role-split content handled by components or specific routes */}
                <Route path="/profile" element={<Profile />} />
                <Route path="/support" element={<Support />} />
                <Route path="/support/compose" element={<SupportCompose />} />
                <Route path="/kyc" element={<KYCVerification />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/sessions" element={<Sessions />} />
                
                {/* Agent Specific Routes */}
                <Route path="/dashboard" element={<AgentDashboard />} />
                <Route path="/clients" element={<AgentClients />} />
                <Route path="/agent-fee" element={<AgentOnboardingFee />} />
                
                {/* Split Routes based on role logic */}
                <Route path="/requests" element={<AuthContextConsumer>
                  {({ user }) => user?.role === 'agent' ? <AgentOrders /> : <RequestListPage />}
                </AuthContextConsumer>} />
                
                <Route path="/history" element={<AuthContextConsumer>
                  {({ user }) => user?.role === 'agent' ? <AgentHistory /> : <History />}
                </AuthContextConsumer>} />

                <Route path="/commissions" element={<AuthContextConsumer>
                  {({ user }) => user?.role === 'agent' ? <AgentCommissions /> : <ClientCommissions />}
                </AuthContextConsumer>} />

                <Route path="/order/:orderId" element={<OrderPage />} />
                <Route path="/order/pay/:orderId" element={<OrderPage />} />
                <Route path="/transaction/:transactionId" element={<TransactionDetailPage />} />
                <Route path="/agent/:agentSlug" element={<AgentProfilePage />} />
              </Route>
            </Route>
          </Route>
          </Routes>
        </Router>
      </NotificationProvider>
      </ToastProvider>
    </WebSocketProvider>
  </AuthProvider>
  );
}

// Helper to consume context in class-like way for conditional routing in JSX
const AuthContextConsumer = ({ children }) => {
  const auth = useAuth();
  return children(auth);
};

export default App;
